// File: apps/web/src/components/collector-keys/GenerateKeyModal.tsx
// Version: 2026.4.1 | Initial — Phase 2 Wave 2 collector-key UI

import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../engine/tokens';
import { SectionLabel } from '../engine/EnginePageShell';
import {
  SUPPORTED_SCOPES,
  type SupportedScope,
  type CreateCollectorKeyRequest,
  type MintedCollectorKey,
} from '../../lib/api/types-collector-keys';
import type { ApiError } from '../../lib/api/types';

interface Props {
  onClose:       () => void;
  onGenerated:   (minted: MintedCollectorKey) => void;
  mint:          (req: CreateCollectorKeyRequest) => Promise<MintedCollectorKey | null>;
  loading:       boolean;
  error:         ApiError | null;
}

export function GenerateKeyModal({ onClose, onGenerated, mint, loading, error }: Props) {
  const [name,     setName]     = useState('');
  const [scopes,   setScopes]   = useState<Set<SupportedScope>>(new Set(['import:claude-code']));
  const [expires,  setExpires]  = useState<string>(''); // empty = never

  const canSubmit = name.trim().length > 0 && scopes.size > 0 && !loading;

  const toggleScope = (id: SupportedScope) => {
    setScopes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    const expiresInDays = expires ? parseInt(expires, 10) : undefined;
    if (expires && (Number.isNaN(expiresInDays!) || expiresInDays! < 1)) return;

    const result = await mint({
      name:          name.trim(),
      scopes:        Array.from(scopes),
      expiresInDays: expiresInDays ?? null,
    });
    if (result) onGenerated(result);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'engine-tooltip-in 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.panel, border: `1px solid ${C.violet}20`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 520,
      }}>
        <SectionLabel color={C.violet}>Generate Collector Key</SectionLabel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <div style={labelStyle}>NAME</div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Laptop — Luke main"
              maxLength={200}
              style={inputStyle}
              autoFocus
            />
            <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 4 }}>
              A label to help you identify this key later. Visible only to you and platform admins.
            </div>
          </div>

          <div>
            <div style={labelStyle}>SCOPES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {SUPPORTED_SCOPES.map(scope => {
                const checked = scopes.has(scope.id);
                return (
                  <label
                    key={scope.id}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      background: checked ? `${C.violet}15` : 'transparent',
                      border: `1px solid ${checked ? `${C.violet}40` : C.faint}`,
                      borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleScope(scope.id)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: F.mono, fontSize: 11, color: checked ? C.violet : C.text }}>
                        {scope.id}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {scope.label} — {scope.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div style={labelStyle}>EXPIRATION (OPTIONAL)</div>
            <input
              value={expires}
              onChange={e => setExpires(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="days — leave blank for never"
              inputMode="numeric"
              style={inputStyle}
            />
            <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 4 }}>
              After this many days, the key will stop working. You can rotate or revoke earlier at any time.
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 16, padding: '10px 14px', borderRadius: 8,
            background: `${C.pink}15`, border: `1px solid ${C.pink}50`,
            color: C.pink, fontFamily: F.mono, fontSize: 11,
          }}>
            {error.status === 403 ? '⚠ ' : '✕ '}
            {error.message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted,
              fontFamily: F.mono, fontSize: 11, padding: '10px 22px', borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
            }}
          >
            CANCEL
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              background: canSubmit ? `${C.violet}20` : `${C.violet}08`,
              border: `1px solid ${canSubmit ? `${C.violet}60` : C.faint}`,
              color: canSubmit ? C.violet : C.dim,
              fontFamily: F.mono, fontSize: 11, padding: '10px 22px', borderRadius: 8,
              cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600,
              letterSpacing: '0.08em',
            }}
          >
            {loading ? 'GENERATING…' : 'GENERATE'}
          </button>
        </div>
      </div>
    </div>
  );
}
